import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Node, PriceListStat } from '../../../../types';
import { Button, Confirmation, Customize, Page, PriceList } from '../../components';
import { PopupSendRequestOEM } from '../../components/popup/popups/send-request-oem';
import { FilesContextType, useFilesContext } from '../../context';
import { Actions } from '../../redux';
import { RootState } from '../../redux/store';
import { defaultItemsPerPage } from '../../shared';
import { usePriceList } from '../../shared/hooks';
import { Car, CarInfo } from '../../types';
import { Search, SelectParts } from './components';
import Step1 from './images/step-1.png';
import Step2 from './images/step-2.png';
import Step3 from './images/step-3.png';
import Step4 from './images/step-4.png';
import WaitingImage from './images/waiting.png';
import { ButtonsStyle, CarInfoTable, Content, Divider, Header, HintText, Step, Title, Waiting } from './style';

type Step = '1' | '2' | '3' | '4';

const stepImages = {
  '1': Step1,
  '2': Step2,
  '3': Step3,
  '4': Step4,
};

const stepTitles = {
  '1': 'Adding a vehicle',
  '2': 'Customize your car',
  '3': 'Major vehicle components',
  '4': 'Price list',
};

interface ButtonsProp {
  step: Step;
  bottom?: boolean;
}

export default () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const { files, clearFiles } = useFilesContext() as FilesContextType;

  const { brandId, brandsList, brandFieldsValues, brandFieldsList, carInfo, carsToSelect, car, sessionId, vin, foundByVin,
    vinNotFound, showSearchButton, searchStatus, nodes, nodesParts, nodesTitles, carId, carsShort } = useSelector((state: RootState) => state.Car);
  const language = useSelector((state: RootState) => state.App.language);
  const config = useSelector((state: RootState) => state.App.config);
  const shop = useSelector((state: RootState) => state.App.shop);
  const user = useSelector((state: RootState) => state.Auth.user);

  const [step, setStep] = useState<Step>('1');
  const [paramsChanged, setParamsChanged] = useState(false);
  const [showCustomizeError, setShowCustomizeError] = useState(false);
  const [showCustomizeErrorConfirmation, setShowCustomizeErrorConfirmation] = useState(false);
  const [priceListStat, setPriceListStat] = useState<PriceListStat | false>(false);

  const customizeIncomplete = useMemo(() => !car?.brandName || !car?.modelName || !car?.year || !car?.fuel || !car?.transmission ||
    !car?.color || !car?.wheelDrive || !car?.body || !car?.tailgate || !car?.doors || !car?.steering, [car]);

  const { loadingStatus, priceList, priceListTotal, page, limit, searchText, order, setPriceList, uploadFiles,
    storePart, deletePart, setPage, setLimit, setSearchText, setOrder, reload } = usePriceList(car?.id || -1, defaultItemsPerPage);

  const setVin = useCallback((value: string) => dispatch(Actions.Car.setVin(value)), [dispatch]);
  const searchByVin = useCallback((vin, callbackError?: () => void) => dispatch(Actions.Car.searchByVin(vin, callbackError)), [dispatch]);
  const getBrandsList = useCallback(() => dispatch(Actions.Car.getBrandsList()), [dispatch]);
  const setBrandId = useCallback((value?: number) => {
    setParamsChanged(true);
    dispatch(Actions.Car.setBrandId(value));
  }, [dispatch]);
  const clearBrandsList = useCallback(() => dispatch(Actions.Car.clearBrandFields()), [dispatch]);
  const getBrandFields = useCallback((brandId: string) => dispatch(Actions.Car.getBrandFields(brandId)), [dispatch]);
  const setBrandField = useCallback((fieldId: number, optionId: number | null, fieldName: string, fieldValue: string, callback: (value: boolean) => void) => {
    setParamsChanged(true);
    dispatch(Actions.Car.setBrandField(fieldId, optionId, fieldName, fieldValue, callback));
  }, [dispatch]);
  const updateCar = useCallback((car: Partial<Car>) => dispatch(Actions.Car.updateCar(car)), [dispatch]);
  const setVinNotFound = useCallback((value: boolean) => dispatch(Actions.Car.setVinNotFound(value)), [dispatch]);
  const setShowSearchButton = useCallback((value: boolean) => dispatch(Actions.Car.setShowSearchButton(value)), [dispatch]);
  const clearBrandData = useCallback(() => dispatch(Actions.Car.clearBrandData()), [dispatch]);
  const setNodes = useCallback((nodes: Node[]) => dispatch(Actions.Car.setNodes(nodes)), [dispatch]);
  const setCarInfo = useCallback((car: CarInfo) => dispatch(Actions.Car.setCarInfo(car)), [dispatch]);
  const setCarId = useCallback((value?: number) => dispatch(Actions.Car.setCarId(value)), [dispatch]);
  const saveData = useCallback((callback?: (carId: number) => void) => dispatch(Actions.Car.saveData(callback)), [dispatch]);
  const loadAllCars = useCallback(() => dispatch(Actions.Car.loadCars({ all: true }, true)), [dispatch]);
  const searchByParams = useCallback(() => {
    if (paramsChanged) {
      dispatch(Actions.Car.searchByParams());
      setParamsChanged(false);
    } else if (carsToSelect && carId !== undefined) {
      // eslint-disable-next-line unicorn/no-useless-undefined
      setCarId(undefined);
    }
  }, [paramsChanged, carsToSelect, carId, dispatch, setCarId]);

  useEffect(() => {
    history.push(`#step-${step}`);
  }, [step, history]);

  useEffect(() => {
    if (step === '3') {
      dispatch(Actions.Car.getNodesParts(language));
    }
  }, [step, language, dispatch]);

  useEffect(() => {
    if (step == '4') {
      dispatch(Actions.Car.getPriceList((stat) => {
        if (user) {
          setPriceListStat(stat);
        }

        saveData(loadAllCars);
      }));
      // dispatch(Actions.Car.getPriceListMock());
    }
  }, [step, user, dispatch, saveData, loadAllCars]);

  useEffect(() => {
    dispatch(Actions.App.loadShop());
  }, [dispatch]);

  const Buttons = memo(({ bottom }: ButtonsProp) => <ButtonsStyle bottom={bottom}>
    {step === '1' && carInfo && <Button
      width='178px'
      onClick={() => dispatch(Actions.Car.getCar({
        sessionId,
        vin: foundByVin ? vin : undefined,
      }, () => setStep('2')))}
    >{t('Accept')}</Button>}
    {step === '2' && <>
      <Button onClick={() => { setStep('1'); setShowCustomizeError(false); }}>{t('Back')}</Button>
      <Button
        width={i18n.language !== 'ES' ? '178px' : undefined}
        onClick={() => {
          if (!customizeIncomplete) {
            dispatch(Actions.Car.uploadFiles(files, (urls: string[]) => {
              dispatch(Actions.Car.updateCar({ images: urls }));
              clearFiles();
            }));
            setStep('3');
          } else {
            setShowCustomizeErrorConfirmation(true);
          }
        }}
        iconRight='next'
      >{t('Next step')}</Button>
    </>}
    {step === '3' && sessionId && <>
      {!bottom && <a id="accept" />}
      <Button onClick={() => setStep('2')}>{t('Back')}</Button>
      <Button width='178px' onClick={() => setStep('4')}>{t('Accept')}</Button>
    </>}
    {step === '4' && !bottom && <Button onClick={() => setStep('3')}>{t('Back')}</Button>}
  </ButtonsStyle>);

  return <>
    {(priceListStat && user) && <PopupSendRequestOEM
      stat={priceListStat}
      user={user}
      vin={vin}
      carId={car?.id}
      onClose={() => setPriceListStat(false)}
    />}
    {showCustomizeErrorConfirmation && <Confirmation
      title={t('Some data is missing')}
      text={t('Fill in the missing fields')}
      buttons={[{
        label: t('Ok'),
        onClick: () => {
          setShowCustomizeError(true);

          setTimeout(() => {
            const element = document.querySelector('.input-error');

            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
            }
          }, 0);

          setShowCustomizeErrorConfirmation(false);
        },
      }]}
      onClose={() => setShowCustomizeErrorConfirmation(false)}
    />}
    <Page loading={loadingStatus === 'loading'} authButtons>
      <Content oneColumn={['3', '4'].includes(step)}>
        <div>
          <Step marginBottom={step === '4' ? 28 : 48} smallTitle={i18n.language === 'ES'}>
            <div>
              <img src={stepImages[step]} />
              <span>{t(stepTitles[step])}</span>
            </div>
            {['3', '4'].includes(step) && <Buttons step={step} />}
          </Step>
          {step === '1' && <Search
            vin={vin}
            brandId={brandId}
            brandsList={brandsList}
            brandFields={brandFieldsValues}
            brandFieldsList={brandFieldsList}
            vinNotFound={vinNotFound}
            showSearchButton={showSearchButton}
            carsToSelect={carsToSelect}
            carId={carId}
            user={user}
            demoVINs={config?.demoVINs}
            setVin={setVin}
            searchByVin={searchByVin}
            searchByParams={searchByParams}
            setBrandId={setBrandId}
            getBrandsList={getBrandsList}
            clearBrandFields={clearBrandsList}
            getBrandFields={getBrandFields}
            setBrandField={setBrandField}
            setVinNotFound={setVinNotFound}
            setShowSearchButton={setShowSearchButton}
            clearBrandData={clearBrandData}
            setCarInfo={setCarInfo}
            setCarId={setCarId}
          />}
          {step === '2' && car && <Customize
            car={car}
            foundByVin={foundByVin}
            showError={showCustomizeError}
            updateCar={updateCar}
            addCar
          />}
          {step === '3' && nodesParts && nodesTitles && <SelectParts
            nodes={nodes}
            nodesParts={nodesParts}
            nodesTitles={nodesTitles}
            setNodes={setNodes}
          />}
          {step === '4' && <PriceList
            title={t('Enter prices and add photos')}
            car={car}
            carsShort={carsShort}
            priceList={priceList || []}
            total={priceListTotal}
            page={page}
            limit={limit}
            order={order}
            searchText={searchText}
            shop={shop}
            setPriceList={setPriceList}
            uploadFiles={uploadFiles}
            storePart={user ? storePart : undefined}
            deletePart={user ? (id: number) => deletePart(id, car?.id) : undefined}
            setPage={setPage}
            setOrder={setOrder}
            setLimit={setLimit}
            reload={reload}
            setSearchText={user ? (text) => { setSearchText(text); setPage(1); } : undefined}
          />}
          {['3', '4'].includes(step) && <Buttons step={step} bottom />}
        </div>
        {['1', '2'].includes(step) && <>
          <Divider marginHorizontal={50} />
          <div>
            <Header>
              <Title key={searchStatus} size='medium' animated={searchStatus === 'done'}>{t('Found cars')}</Title>
              <div>
                <Buttons step={step} />
              </div>
            </Header>
            {!carInfo && <>
              <HintText marginTop={49} marginBottom={34}>{t('Your search result will be displayed here after entering data')}</HintText>
              <Waiting src={WaitingImage} />
            </>}
            {carInfo && <CarInfoTable>
              <table>
                <tbody>
                  {carInfo.map(({ name, value }, i) => <tr key={i}>
                    <td><div>{name}</div></td>
                    <td><div>{value}</div></td>
                  </tr>)}
                </tbody>
              </table>
            </CarInfoTable>}
            {step === '2' && <Buttons step={step} bottom />}
          </div>
        </>}
      </Content>
    </Page>
  </>;
};
